import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { formatExactDateFr, formatRelativeTimeFr } from "../Helper/dates";
import { levelIndicatorClass, levelLabel, logTagStyle } from "../Helper/logs";
import { useLogsData } from "../hooks/useLogsData";
import { useUserDirectory } from "../Components/Account/useUserDirectory";
import { UserAvatar } from "../Components/UserAvatar";
import type { LogEntry } from "../API/Models";

const normalizeTags = (value: string) =>
	value
		.split(",")
		.map((tag) => tag.trim().toLowerCase())
		.filter((tag, index, arr) => tag.length > 0 && arr.indexOf(tag) === index);

const LEVEL_OPTIONS = [
	{ value: "all", label: "Tous les niveaux" },
	{ value: "information", label: "Information" },
	{ value: "warning", label: "Avertissement" },
	{ value: "error", label: "Erreur" },
	{ value: "debug", label: "Débogage" }
];

const LIMIT_OPTIONS = [50, 100, 150, 200];

/** Valeur retardee de `delayMs` : les filtres texte ne requetent pas le serveur a chaque frappe. */
const useDebounced = (value: string, delayMs: number): string => {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(t);
	}, [value, delayMs]);
	return debounced;
};

/** Nombre de tags affichés inline sur la ligne compacte : le reste passe en « +n » (tout est
 *  visible dans le détail déplié). */
const INLINE_TAGS = 2;

const isSystemUser = (raw?: string | null): boolean => {
	const s = (raw ?? "").trim();
	return s.length === 0 || s.toUpperCase() === "SYSTEM";
};

/** Colonne acteur : avatar + nom sur UNE ligne, cliquable vers /account/{id} quand le profil résout. */
const ActorCell: React.FC<{
	user?: string | null;
	resolveId: (n?: string | null) => string | null;
	resolvePicture: (n?: string | null) => string | null;
}> = ({ user, resolveId, resolvePicture }) => {
	if (isSystemUser(user)) {
		return (
			<span className="inline-flex min-w-0 items-center gap-2 text-slate-400">
				<UserAvatar name="Système" sizeClass="h-6 w-6" textClass="text-[9px]" />
				<span className="truncate text-xs">Système</span>
			</span>
		);
	}
	const name = (user ?? "").trim();
	const id = resolveId(name);
	const inner = (
		<>
			<UserAvatar name={name} id={id ?? undefined} pictureUrl={resolvePicture(name)} sizeClass="h-6 w-6" textClass="text-[9px]" />
			<span className="truncate text-xs font-medium text-slate-700">{name}</span>
		</>
	);
	return id ? (
		<Link
			to={`/account/${id}`}
			onClick={(e) => e.stopPropagation()}
			className="inline-flex min-w-0 max-w-full items-center gap-2 rounded transition hover:text-sky-700 hover:underline"
			title={`Voir le profil de ${name}`}
		>
			{inner}
		</Link>
	) : (
		<span className="inline-flex min-w-0 max-w-full items-center gap-2" title={name}>
			{inner}
		</span>
	);
};

/** Détail déplié d'une entrée : message complet, détails, tous les tags, dates exactes. */
const ExpandedRow: React.FC<{ entry: LogEntry }> = ({ entry }) => (
	<tr className="bg-slate-50/70">
		<td colSpan={6} className="px-5 py-3">
			<div className="space-y-2 pl-6 text-sm">
				<p className="font-semibold text-slate-800">{entry.message || "Aucun message"}</p>
				{entry.details && <p className="whitespace-pre-wrap break-words text-xs text-slate-600">{entry.details}</p>}
				{entry.tags && entry.tags.length > 0 && (
					<div className="flex flex-wrap items-center gap-1.5">
						{entry.tags.map((tag, index) => (
							<span
								key={`${entry.id}-x-${tag}-${index}`}
								className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${logTagStyle(tag)}`}
							>
								{tag ?? "sans tag"}
							</span>
						))}
					</div>
				)}
				<p className="text-[11px] text-slate-400">
					Créé : {formatExactDateFr(entry.createdAt)}
					{entry.updatedAt && entry.updatedAt !== entry.createdAt && <> · Mis à jour : {formatExactDateFr(entry.updatedAt)}</>}
				</p>
			</div>
		</td>
	</tr>
);

/**
 * Journal système : chaque entrée tient sur UNE seule ligne compacte (niveau, message tronqué, type,
 * tags repliés, acteur avec avatar, horodatage relatif) ; un clic déplie le détail complet
 * (message entier, détails, tous les tags, dates exactes). Filtres niveau/type/tags/recherche.
 */
const Logs: React.FC = () => {
	const [limit, setLimit] = useState(150);
	const [includeDebug, setIncludeDebug] = useState(true);
	const [levelFilter, setLevelFilter] = useState("all");
	const [typeFilter, setTypeFilter] = useState("");
	const [tagFilter, setTagFilter] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	const { resolveId, resolvePicture } = useUserDirectory();

	const activeTags = useMemo(() => normalizeTags(tagFilter), [tagFilter]);

	// Filtres SERVEUR : le journal est filtre en base (niveau, type, texte) puis pagine. Sans ca,
	// un niveau rare (Debogage, Erreur) paraissait vide : la page ne chargeait que les N dernieres
	// entrees toutes confondues et filtrait cote client. Les saisies texte sont debouncees pour ne
	// pas requeter a chaque frappe ; le filtre client reste comme raffinement instantane.
	const debouncedType = useDebounced(typeFilter, 400);
	const debouncedSearch = useDebounced(searchTerm, 400);

	const {
		logs,
		loading,
		error,
		lastUpdated,
		isRefreshing,
		refresh
	} = useLogsData({ limit, includeDebug, tags: activeTags, level: levelFilter, type: debouncedType, search: debouncedSearch });

	const filteredLogs = useMemo(() => {
		const normalizedLevel = levelFilter.toLowerCase();
		const normalizedType = typeFilter.trim().toLowerCase();
		const normalizedSearch = searchTerm.trim().toLowerCase();

		return logs.filter((entry) => {
			const entryLevel = (entry.level ?? "").toLowerCase();
			const entryType = (entry.type ?? "").toLowerCase();
			const entryText = `${entry.message ?? ""} ${entry.details ?? ""}`.toLowerCase();
			const entryTags = (entry.tags ?? []).map((tag) => (tag ?? "").toLowerCase());

			const matchesLevel = normalizedLevel === "all" ? true : entryLevel === normalizedLevel;
			const matchesType = normalizedType ? entryType.includes(normalizedType) : true;
			const matchesTags = activeTags.length === 0 ? true : activeTags.every((tag) => entryTags.includes(tag));
			const matchesSearch = normalizedSearch ? entryText.includes(normalizedSearch) : true;

			return matchesLevel && matchesType && matchesTags && matchesSearch;
		});
	}, [activeTags, levelFilter, logs, searchTerm, typeFilter]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Logs</p>
						<h1 className="text-2xl font-semibold text-slate-900">Journal système</h1>
						{lastUpdated && (
							<p className="text-xs text-slate-500">
								Dernière lecture : {formatExactDateFr(lastUpdated)} ({formatRelativeTimeFr(lastUpdated)})
							</p>
						)}
					</div>
					<button
						type="button"
						onClick={refresh}
						disabled={isRefreshing}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						{isRefreshing ? (
							<>
								<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
								Actualisation…
							</>
						) : (
							"Actualiser"
						)}
					</button>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
					<div className="grid gap-4 lg:grid-cols-4">
						<div className="space-y-1.5">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Niveau</label>
							<select
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={levelFilter}
								onChange={(event) => setLevelFilter(event.target.value)}
							>
								{LEVEL_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-1.5">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type</label>
							<input
								type="text"
								placeholder="ex. content-save"
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={typeFilter}
								onChange={(event) => setTypeFilter(event.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tags</label>
							<input
								type="text"
								placeholder="preview,production"
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={tagFilter}
								onChange={(event) => setTagFilter(event.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recherche</label>
							<input
								type="text"
								placeholder="Message ou détails"
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value)}
							/>
						</div>
					</div>
					<div className="mt-4 flex flex-wrap items-center gap-4">
						<label className="inline-flex items-center gap-2 text-sm text-slate-700">
							<input
								type="checkbox"
								className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
								checked={includeDebug}
								onChange={(event) => setIncludeDebug(event.target.checked)}
							/>
							<span>Inclure le débogage</span>
						</label>
						<label className="inline-flex items-center gap-2 text-sm text-slate-700">
							<span>Limite</span>
							<select
								className="rounded-lg border border-slate-200 px-3 py-1 text-sm focus:border-slate-500 focus:outline-none"
								value={limit}
								onChange={(event) => setLimit(Number(event.target.value))}
							>
								{LIMIT_OPTIONS.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</label>
						<button
							type="button"
							className="text-sm font-medium text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-900"
							onClick={() => {
								setLevelFilter("all");
								setTypeFilter("");
								setTagFilter("");
								setSearchTerm("");
							}}
						>
							Réinitialiser les filtres
						</button>
					</div>
				</section>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-3">
						<p className="text-sm text-slate-500">
							{filteredLogs.length} entrée{filteredLogs.length > 1 ? "s" : ""} affichée{filteredLogs.length > 1 ? "s" : ""} sur {logs.length} chargée{logs.length > 1 ? "s" : ""}
						</p>
					</header>
					{error && (
						<div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
							{error}
						</div>
					)}
					<div className="overflow-x-auto">
						<table className="w-full table-fixed divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="w-9 px-3 py-2.5" aria-label="Niveau" />
									<th className="px-2 py-2.5">Message</th>
									<th className="w-36 px-2 py-2.5">Type</th>
									<th className="w-44 px-2 py-2.5">Tags</th>
									<th className="w-44 px-2 py-2.5">Acteur</th>
									<th className="w-28 px-3 py-2.5 text-right">Quand</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={6} className="px-5 py-6 text-center text-slate-500">
											Chargement du journal…
										</td>
									</tr>
								) : filteredLogs.length === 0 ? (
									<tr>
										<td colSpan={6} className="px-5 py-10 text-center text-slate-500">
											Aucune entrée ne correspond aux filtres.
										</td>
									</tr>
								) : (
									filteredLogs.map((entry, index) => {
										const expanded = expandedIndex === index;
										const inlineTags = (entry.tags ?? []).slice(0, INLINE_TAGS);
										const extraTags = (entry.tags?.length ?? 0) - inlineTags.length;
										const entryLevel = (entry.level ?? "").toLowerCase();
										const isError = entryLevel === "error";
										const isWarning = entryLevel === "warning";
										const rowAccent = isError
											? "border-l-2 border-rose-500 bg-rose-50/50"
											: isWarning
												? "border-l-2 border-amber-400 bg-amber-50/40"
												: "";
										return (
											<React.Fragment key={entry.id ?? `log-${index}`}>
												<tr
													onClick={() => setExpandedIndex(expanded ? null : index)}
													className={`cursor-pointer transition hover:bg-slate-50 ${rowAccent} ${expanded ? "bg-slate-50" : ""}`}
													title={entry.message ?? ""}
												>
													<td className="px-3 py-2">
														<span className="inline-flex items-center gap-1.5">
															{expanded ? (
																<FiChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
															) : (
																<FiChevronRight className="h-3 w-3 shrink-0 text-slate-300" />
															)}
															<span
																className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${levelIndicatorClass(entry.level ?? "")}`}
																title={levelLabel(entry.level)}
															/>
														</span>
													</td>
													<td className="truncate whitespace-nowrap px-2 py-2 font-medium text-slate-800">
														{entry.message || "Aucun message"}
														{entry.details && <span className="ml-2 font-normal text-slate-400">{entry.details}</span>}
													</td>
													<td className="truncate whitespace-nowrap px-2 py-2 text-xs text-slate-500" title={entry.type ?? ""}>
														{entry.type || "-"}
													</td>
													<td className="whitespace-nowrap px-2 py-2">
														<span className="inline-flex items-center gap-1 overflow-hidden">
															{inlineTags.length === 0 ? (
																<span className="text-xs text-slate-300">-</span>
															) : (
																inlineTags.map((tag, index) => (
																	<span
																		key={`${entry.id}-${tag}-${index}`}
																		className={`inline-flex max-w-[5.5rem] items-center truncate rounded-full border px-2 py-0.5 text-[10px] font-semibold ${logTagStyle(tag)}`}
																	>
																		{tag ?? "sans tag"}
																	</span>
																))
															)}
															{extraTags > 0 && (
																<span className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
																	+{extraTags}
																</span>
															)}
														</span>
													</td>
													<td className="whitespace-nowrap px-2 py-2">
														<ActorCell user={entry.user} resolveId={resolveId} resolvePicture={resolvePicture} />
													</td>
													<td className="whitespace-nowrap px-3 py-2 text-right text-xs text-slate-400" title={formatExactDateFr(entry.createdAt)}>
														{formatRelativeTimeFr(entry.createdAt)}
													</td>
												</tr>
												{expanded && <ExpandedRow entry={entry} />}
											</React.Fragment>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</section>
			</div>
		</div>
	);
};

export default Logs;
